import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Container, Form, InputGroup, Modal, Row, Table, Pagination } from 'react-bootstrap';

const commissionMatrix = [
  {
    operator: 'Prepaid Mobile',
    opCode: 'MOB001',
    commission: 2.5,
    type: 'Percent'
  },
  {
    operator: 'DTH Recharge',
    opCode: 'DTH104',
    commission: 18,
    type: 'Flat'
  },
  {
    operator: 'Electricity Board',
    opCode: 'ELE222',
    type: 'Percent',
    slabs: [
      { name: '0 - 1,000', charge: '0.90%' },
      { name: '1,001 - 5,000', charge: '1.25%' },
      { name: '5,001 & Above', charge: '1.45%' }
    ]
  },
  {
    operator: 'Water Utility',
    opCode: 'WTR089',
    commission: 9,
    type: 'Flat'
  },
  {
    operator: 'Gas Cylinder',
    opCode: 'GAS311',
    commission: 1.75,
    type: 'Percent'
  },
  {
    operator: 'Broadband Services',
    opCode: 'BRDB07',
    commission: 2,
    type: 'Percent'
  },
  {
    operator: 'Insurance Premium',
    opCode: 'INS518',
    type: 'Flat',
    slabs: [
      { name: 'Standard Policies', charge: '₹18.00' },
      { name: 'Premium Policies', charge: '₹22.00' },
      { name: 'Group Policies', charge: '₹26.00' }
    ]
  },
  {
    operator: 'Loan EMI',
    opCode: 'EMI642',
    commission: 1.1,
    type: 'Percent'
  },
  {
    operator: 'Fastag Recharge',
    opCode: 'FST330',
    commission: 14,
    type: 'Flat'
  },
  {
    operator: 'Municipal Tax',
    opCode: 'MUN905',
    commission: 1.4,
    type: 'Percent'
  }
];

const ApiPartnerMyCommissionCharges = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [slabModalState, setSlabModalState] = useState({
    show: false,
    operator: '',
    slabs: []
  });
  const rowsPerPage = 6;

  const filteredMatrix = useMemo(() => {
    return commissionMatrix.filter((row) => {
      const matchesSearch =
        !searchQuery ||
        Object.values(row)
          .join(' ')
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [searchQuery]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentMatrix = filteredMatrix.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredMatrix.length / rowsPerPage) || 1;

  const formatCommission = (value, type) => {
    if (type === 'Percent') {
      return `${value.toFixed(2)}%`;
    }
    return `₹${value.toFixed(2)}`;
  };

  const handleOpenSlabModal = (row) => {
    setSlabModalState({
      show: true,
      operator: row.operator,
      slabs: row.slabs || []
    });
  };

  const handleCloseSlabModal = () => {
    setSlabModalState((prev) => ({ ...prev, show: false }));
  };

  return (
    <Container fluid className="my-commission-page p-4">
      <Row>
        <Col>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom" style={{ padding: '16px 24px' }}>
              <Row className="align-items-center g-3">
                <Col lg={6} md={6} sm={12}>
                  <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c2268' }}>
                    My Commission / Charges
                  </h5>
                </Col>
                <Col lg={6} md={6} sm={12} className="d-flex justify-content-end">
                  <InputGroup style={{ maxWidth: '320px' }}>
                    <InputGroup.Text style={{ backgroundColor: '#f8f9fa', border: '1px solid #ddd' }}>
                      <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search operator or code..."
                      value={searchQuery}
                      onChange={(event) => {
                        setSearchQuery(event.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ border: '1px solid #ddd' }}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      {['Operator', 'Commission', 'Op Code', 'Is Percent / Flat'].map((column) => (
                        <th
                          key={column}
                          style={{
                            padding: '14px 18px',
                            fontWeight: 600,
                            color: '#555',
                            borderBottom: '2px solid #dee2e6',
                            fontSize: '15px',
                            textAlign: 'center'
                          }}
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentMatrix.length > 0 ? (
                      currentMatrix.map((row) => (
                        <tr key={row.opCode} style={{ transition: 'background-color 0.2s' }}>
                          <td style={{ padding: '14px 18px', color: '#333', fontSize: '15px', fontWeight: 600, textAlign: 'center' }}>
                            {row.operator}
                          </td>
                          <td style={{ padding: '14px 18px', color: '#198754', fontSize: '15px', fontWeight: 600, textAlign: 'center' }}>
                            {row.slabs?.length ? (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleOpenSlabModal(row)}
                                style={{ fontSize: '12px', padding: '2px 10px' }}
                              >
                                Slabs
                              </Button>
                            ) : (
                              formatCommission(row.commission, row.type)
                            )}
                          </td>
                          <td style={{ padding: '14px 18px', color: '#666', fontSize: '15px', textAlign: 'center' }}>{row.opCode}</td>
                          <td style={{ padding: '14px 18px', color: '#000', fontSize: '15px', fontWeight: 700, textAlign: 'center' }}>
                            {row.slabs?.length ? 'Slab' : row.type === 'Percent' ? '%' : '₹'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center py-4" style={{ color: '#999' }}>
                          No commission slabs found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="bg-white border-top" style={{ padding: '12px 24px' }}>
              <Row className="align-items-center">
                <Col md={6} style={{ fontSize: '14px', color: '#666' }}>
                  Showing {filteredMatrix.length === 0 ? 0 : indexOfFirstRow + 1} to{' '}
                  {Math.min(indexOfLastRow, filteredMatrix.length)} of {filteredMatrix.length}
                </Col>
                <Col md={6} className="d-flex justify-content-end">
                  <Pagination className="mb-0">
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    />
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      <style jsx="true">{`
        .my-commission-page {
          background-color: #fff;
          min-height: calc(100vh - 100px);
        }

        .table tbody tr:hover {
          background-color: #f8f9fa;
        }

        @media (max-width: 768px) {
          .table-responsive {
            overflow-x: auto;
          }
        }
      `}</style>

      <Modal show={slabModalState.show} onHide={handleCloseSlabModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{slabModalState.operator || 'Commission'} Slabs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {slabModalState.slabs.length > 0 ? (
            <Table striped bordered hover size="sm" className="mb-0" style={{ fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', fontSize: '13px' }}>Slab Name</th>
                  <th style={{ textAlign: 'center', fontSize: '13px' }}>Charge</th>
                </tr>
              </thead>
              <tbody>
                {slabModalState.slabs.map((slab, index) => (
                  <tr key={`${slab.name}-${index}`}>
                    <td style={{ textAlign: 'center', fontWeight: 600, fontSize: '13px' }}>{slab.name}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: '#198754', fontSize: '13px' }}>{slab.charge}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-3" style={{ color: '#666', fontWeight: 500 }}>
              No slabs configured for this operator.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSlabModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ApiPartnerMyCommissionCharges;

